/**
 * Context-aware assistant, mounted once next to the main menu. Renders
 * nothing until the access probe passes — reads the shared probe
 * (`useAgentAccessContext`, `AgentAccessProvider.tsx`) rather than calling
 * `useAgentAccess` itself, so this panel and every "AI suggestions"
 * button (`useAgentAvailable`) share exactly one `agentAccess` query. It
 * knows which page/entity the user is looking at (`pageContext.ts`), and
 * shows the conversation through MUI X Chat (`AgentChatBox`); this file is
 * the shell: header, drag/resize, the access gate, and bridging
 * drops/pastes and `useAgentPanelControl().ask()` into the box's composer.
 * Reading answers aloud is controlled per message (each answer's own "Read
 * aloud" action, in `AgentChatBox`), not from this header.
 */
import {
  AddComment as NewChatIcon,
  Close as CloseIcon,
  CloseFullscreen as CloseFullscreenIcon,
  DragIndicator as DragIcon,
  History as HistoryIcon,
  OpenInFull as OpenInFullIcon,
  SmartToy as AssistantIcon,
} from "@mui/icons-material";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Fab,
  Fade,
  IconButton,
  Modal,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  type Theme,
} from "@mui/material";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import type { ChatAttachmentRejection } from "@mui/x-chat/headless";
import { useFormatting, useScreenWidth } from "@msys/ui";
import {
  floatActionMarginRight,
  usePublishAssistantLauncherOffset,
} from "../../commons/layout/assistantLauncherOffset.js";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { useLocation } from "react-router-dom";
import { pastedAgentFileName } from "./AgentAttachments.js";
import { useAgentAccessContext } from "./AgentAccessProvider.js";
import {
  AgentChatBox,
  describeRejections,
  type AgentChatOutbox,
} from "./AgentChatBox.js";
import { agentHeaderBackground } from "./agentSurfaceColors.js";
import { subscribeToAgentPanelAsk } from "./agentPanelControl.js";
import { useAgentSpeech } from "./agentSpeechOutput.js";
import { summarizeSessionCost } from "./agentSessionCost.js";
import {
  resolveStoredSpeechCostUsd,
  splitAnswerCost,
} from "./agentSpeechCost.js";
import { derivePageContext } from "./pageContext.js";
import { useAgentIdentityKey } from "./useAgentAccess.js";
import { useAgentChat } from "./useAgentChat.js";
import { useElementWidth } from "./useElementWidth.js";
import { PANEL_MARGIN, conversationListLayout } from "./agentPanelGeometry.js";
import { useAgentPanelGeometry } from "./useAgentPanelGeometry.js";
import { agentPanelPanes, type AgentPanelPane } from "./views/panelPanes.js";

const CONVERSATION_LIST_KEY = "msys-agent-chat-list";

/** Whether the conversation list is shown next to the thread on a wide panel; remembered per browser. */
function useConversationListVisible(): [boolean, () => void] {
  const [visible, setVisible] = React.useState(() => {
    try {
      return window.localStorage.getItem(CONVERSATION_LIST_KEY) !== "hidden";
    } catch {
      return true;
    }
  });
  const toggle = React.useCallback(
    () =>
      setVisible(current => {
        try {
          window.localStorage.setItem(
            CONVERSATION_LIST_KEY,
            current ? "hidden" : "shown"
          );
        } catch {
          // Storage blocked: the choice is just not remembered.
        }
        return !current;
      }),
    []
  );
  return [visible, toggle];
}

/**
 * One registered pane's header button. Kept as its own component (rather
 * than inlining `pane.useVisible()`/`pane.useBadge()` in a `.map()`) so each
 * pane's hooks are called unconditionally by a stable component instance,
 * not from inside a callback.
 */
function PaneHeaderButton({
  pane,
  active,
  onToggle,
}: {
  pane: AgentPanelPane;
  active: boolean;
  onToggle: (id: string) => void;
}) {
  const visible = pane.useVisible();
  const badge = pane.useBadge?.() ?? false;
  const title = pane.useTitle();
  if (!visible) return null;

  const Icon = pane.icon;
  return (
    <Tooltip title={title}>
      <IconButton
        size="small"
        color={active ? "primary" : "default"}
        aria-label={title}
        onClick={() => onToggle(pane.id)}
      >
        <Badge color="error" variant="dot" invisible={!badge}>
          <Icon fontSize="small" />
        </Badge>
      </IconButton>
    </Tooltip>
  );
}

/** The panel layer's own z-index: above the page's dialogs (`theme.zIndex.modal`). */
function panelZIndex(theme: Theme): number {
  return theme.zIndex.modal + 1;
}

/**
 * The theme the panel's *content* renders with: every z-index layer moved
 * above the panel's own layer (`panelZIndex`). A dialog, menu, Select or
 * Autocomplete dropdown opened from inside the panel (the delete
 * confirmation, a view's form fields, a pane's dialogs) portals to
 * `document.body` like the panel itself, and at the regular
 * `theme.zIndex.modal` it would open *behind* the panel. A portal reads the
 * nearest `ThemeProvider` up the React tree, so nesting one around the
 * panel's content raises exactly those overlays, while the panel's own
 * layer (its `sx`, resolved against the outer theme) stays at
 * `panelZIndex` — above the page's dialogs. Every layer moves by the same
 * offset, so their order among each other (tooltip over modal, …) is kept.
 */
function raiseOverlayZIndex(outerTheme: Theme): Theme {
  const { zIndex } = outerTheme;
  const offset = panelZIndex(outerTheme) + 1 - zIndex.mobileStepper;
  return {
    ...outerTheme,
    zIndex: {
      mobileStepper: zIndex.mobileStepper + offset,
      fab: zIndex.fab + offset,
      speedDial: zIndex.speedDial + offset,
      appBar: zIndex.appBar + offset,
      drawer: zIndex.drawer + offset,
      modal: zIndex.modal + offset,
      snackbar: zIndex.snackbar + offset,
      tooltip: zIndex.tooltip + offset,
    },
  };
}

export function AgentPanel() {
  const { t } = useTranslate(["Assistant", "Global", "AttachmentField"]);
  const { getFormattedFloat } = useFormatting();
  const outerTheme = useTheme();
  const overlayTheme = React.useMemo(
    () => raiseOverlayZIndex(outerTheme),
    [outerTheme]
  );
  const location = useLocation();
  const access = useAgentAccessContext();
  const identityKey = useAgentIdentityKey();
  const chat = useAgentChat({ resetKey: identityKey });
  const [speechCostByMessageId, setSpeechCostByMessageId] = React.useState<
    Record<string, number>
  >({});
  // A live cost per stored answer, kept in sync with `chat.messages` below —
  // read by `AgentChatBox`'s `MetaPart` in place of the frozen `costUsd`
  // its `initialMessages` seeded at mount (`agentChatAdapter.js`'s
  // `resolveAnswerCostUsd`), so a replayed answer's cost (booked server-side
  // and picked up by `chat.refreshConversation()` below) reaches that
  // answer's own caption, not only the header's session total.
  const [costOverrideByMessageId, setCostOverrideByMessageId] = React.useState<
    Record<string, number | null>
  >({});
  // Each stored answer's cost the first time it was ever observed — never
  // touched again (mirrors `AgentChatBox`'s own frozen `meta.costUsd`) — the
  // baseline `resolveStoredSpeechCostUsd` (`agentSpeechCost.ts`) compares
  // `costOverrideByMessageId`'s current entry against below, to tell "not
  // yet reconciled" (still equal) apart from "a stored replay since folded a
  // previously booked live speech cost into it" (moved past this baseline).
  const settledCostUsdByMessageId = React.useRef<Record<string, number | null>>(
    {}
  );
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const speech = useAgentSpeech({
    available: access.speech,
    conversationId: () => chat.conversationId,
    onCost: (messageId, costUsd) =>
      setSpeechCostByMessageId(current => ({
        ...current,
        [messageId]: (current[messageId] ?? 0) + costUsd,
      })),
    // A replayed stored answer's cost is booked server-side directly onto
    // the message, with no client-side `onCost` call — refresh the open
    // conversation in the background so its refreshed `costUsd` feeds the
    // session cost again, without a manual reload (a no-op without one, and
    // does not disturb an in-flight turn's draft — `useAgentChat.ts`).
    onStoredReplayFinished: () => void chat.refreshConversation(),
    // A reading that ended without a sound (every piece failed or came back
    // empty) says so, instead of leaving the user waiting in silence.
    onNothingHeard: () =>
      enqueueSnackbar(
        t("This answer could not be read aloud. Please try again.", {
          ns: "Assistant",
        }),
        { variant: "warning" }
      ),
  });

  // A still-playing/scheduled speech session (and its booked costs) belongs
  // to the identity that started it — `useAgentChat` resets its own state on
  // the same `identityKey` change (`resetKey`), but never touches `speech`,
  // so without this it would keep sounding/booking for the new identity.
  React.useEffect(() => {
    speech.stop();
    setSpeechCostByMessageId({});
    setCostOverrideByMessageId({});
    settledCostUsdByMessageId.current = {};
    // `identityKey` alone decides when to reset; `speech` is a fresh object
    // each render and would otherwise re-run this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityKey]);

  // Keeps each stored answer's cost override in sync with `chat.messages`
  // (updated live on every turn, and in the background by
  // `chat.refreshConversation()` once a stored replay's cost is booked) —
  // `MetaPart` reads it in place of the cost frozen into `AgentChatBox`'s
  // `initialMessages` at mount.
  React.useEffect(() => {
    setCostOverrideByMessageId(current => {
      let changed = false;
      const next = { ...current };
      for (const message of chat.messages) {
        if (message.role !== "assistant" || !message.id) continue;
        const costUsd = message.costUsd ?? null;
        if (!(message.id in settledCostUsdByMessageId.current)) {
          settledCostUsdByMessageId.current[message.id] = costUsd;
        }
        if (next[message.id] !== costUsd) {
          next[message.id] = costUsd;
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [chat.messages]);

  const [isOpen, setIsOpen] = React.useState(false);
  // The panel's layer mounts on first open (no chat box, and so no agent
  // query, for a user who never opens it) and then stays mounted.
  const [everOpened, setEverOpened] = React.useState(false);
  const [modalEpoch, setModalEpoch] = React.useState(0);
  const panel = useAgentPanelGeometry();
  const [showConversations, toggleConversations] = useConversationListVisible();
  // The history goes beside the thread only on a panel wide enough for
  // both; on a narrower one it is an overlay over the thread, closed until
  // the history button opens it — the remembered choice above is for wide
  // panels and never opens it over the thread by itself.
  const [panelElement, setPanelElement] = React.useState<HTMLDivElement | null>(
    null
  );
  const listLayout = conversationListLayout(useElementWidth(panelElement));
  const [overlayRequested, setOverlayRequested] = React.useState(false);
  if (listLayout === "sidebar" && overlayRequested) setOverlayRequested(false);
  const closeConversationOverlay = React.useCallback(
    () => setOverlayRequested(false),
    []
  );
  const historyOpen =
    listLayout === "overlay" ? overlayRequested : showConversations;
  const toggleHistory =
    listLayout === "overlay"
      ? () => setOverlayRequested(open => !open)
      : toggleConversations;
  const [chatEpoch, setChatEpoch] = React.useState(0);
  // The epoch whose box takes the focus as it mounts: a new conversation
  // only, never an identity switch or the first open.
  const [focusEpoch, setFocusEpoch] = React.useState(-1);
  const [activePaneId, setActivePaneId] = React.useState<string | null>(null);
  // Kept as the rejections themselves, not a finished sentence, so the
  // notice is worded in whatever language is shown while it is visible.
  const [rejectedAttachments, setRejectedAttachments] = React.useState<
    ChatAttachmentRejection[] | null
  >(null);
  const [dragDepth, setDragDepth] = React.useState(0);
  const outbox = React.useRef<AgentChatOutbox>({
    send: null,
    pending: null,
    addFiles: null,
    switchConversation: null,
    pendingConversation: null,
  });
  const { backgroundTurns } = chat;
  // Re-renders the history badge whenever a turn starts, moves or ends.
  React.useSyncExternalStore(
    backgroundTurns.subscribe,
    backgroundTurns.getVersion
  );

  const context = React.useMemo(
    () => derivePageContext(location.pathname, location.search),
    [location.pathname, location.search]
  );

  const attachFiles = (files: readonly File[] | FileList | null) => {
    if (!files || files.length === 0) return;
    outbox.current.addFiles?.(Array.from(files));
  };

  const carriesFiles = (event: React.DragEvent) =>
    Array.from(event.dataTransfer.types).includes("Files");
  const dropHandlers = {
    onDragEnter: (event: React.DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      setDragDepth(depth => depth + 1);
    },
    onDragOver: (event: React.DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
    },
    onDragLeave: (event: React.DragEvent) => {
      if (!carriesFiles(event)) return;
      setDragDepth(depth => Math.max(0, depth - 1));
    },
    onDrop: (event: React.DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      setDragDepth(0);
      attachFiles(event.dataTransfer.files);
    },
    onPaste: (event: React.ClipboardEvent) => {
      const files = Array.from(event.clipboardData.files);
      if (files.length === 0) return;
      event.preventDefault();
      attachFiles(files.map(file => pastedAgentFileName(file)));
    },
  };

  // Kept in a ref (not the effect's dep array) so the subscription below
  // stays alive across every render instead of tearing down and
  // resubscribing on each one.
  const askRef = React.useRef((prompt: string) => {
    const { send, pending } = outbox.current;
    if (send) send(prompt);
    else outbox.current.pending = pending ? `${pending}\n\n${prompt}` : prompt;
  });

  const newChat = () => {
    chat.newChat();
    speech.stop();
    setChatEpoch(e => e + 1);
    setFocusEpoch(chatEpoch + 1);
    setRejectedAttachments(null);
    setActivePaneId(null);
    setOverlayRequested(false);
  };

  const openPanel = () => {
    setIsOpen(true);
    setEverOpened(true);
  };

  const closePanel = () => {
    setIsOpen(false);
    setActivePaneId(null);
  };

  /** The finished notice's "Open": the panel, on the conversation whose answer is ready. */
  const openFinishedConversationRef = React.useRef((_id: string) => {});
  openFinishedConversationRef.current = (conversationId: string) => {
    setIsOpen(true);
    setEverOpened(true);
    setActivePaneId(null);
    const { switchConversation } = outbox.current;
    if (switchConversation) switchConversation(conversationId);
    else outbox.current.pendingConversation = conversationId;
  };

  const historyOpenRef = React.useRef(historyOpen);
  historyOpenRef.current = historyOpen;
  // An answer that finished while its chat was not shown: a notice with
  // "Open" (the history badge keeps it until the chat or the history is
  // looked at). Listed only for this identity: `useAgentChat` clears the
  // registry on a switch.
  React.useEffect(
    () =>
      backgroundTurns.onFinished(({ conversationId, title }) => {
        if (historyOpenRef.current) backgroundTurns.markSeen(conversationId);
        enqueueSnackbar(
          t("The answer to “{question}” is ready", {
            ns: "Assistant",
            question: title,
          }),
          {
            variant: "success",
            // A custom action replaces the provider's own close button.
            action: key => (
              <>
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    closeSnackbar(key);
                    openFinishedConversationRef.current(conversationId);
                  }}
                >
                  {t("Open", { ns: "Assistant" })}
                </Button>
                <IconButton
                  color="inherit"
                  size="small"
                  aria-label={t("Close", { ns: "Global" })}
                  onClick={() => closeSnackbar(key)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            ),
          }
        );
      }),
    [backgroundTurns, closeSnackbar, enqueueSnackbar, t]
  );

  // The history shows every chat with its own running marker: nothing
  // finished is unseen once it is open.
  React.useEffect(() => {
    if (historyOpen) backgroundTurns.markAllSeen();
  }, [historyOpen, backgroundTurns]);

  // The history badge: chats running unshown, and answers finished unseen.
  const shownConversationId =
    backgroundTurns.attached()?.conversationId ?? chat.conversationId;
  const runningElsewhere = backgroundTurns
    .runningConversationIds()
    .filter(id => id !== shownConversationId).length;
  const unseenFinished = backgroundTurns.unseenFinishedIds().length;
  const historyActivity = [
    unseenFinished > 0
      ? t("{count, plural, one {# answer ready} other {# answers ready}}", {
          ns: "Assistant",
          count: unseenFinished,
        })
      : null,
    runningElsewhere > 0
      ? t(
          "{count, plural, one {# conversation running in the background} other {# conversations running in the background}}",
          { ns: "Assistant", count: runningElsewhere }
        )
      : null,
  ]
    .filter((line): line is string => line !== null)
    .join(" · ");

  // Read by the long-lived subscription below, which must reach this
  // render's `newChat` rather than the one it was created with.
  const askInNewChatRef = React.useRef((_prompt: string) => {});
  askInNewChatRef.current = (prompt: string) => {
    newChat();
    // Remount the panel's layer so it registers as the topmost modal again:
    // a dialog opened after the panel (the one whose button asked) would
    // otherwise keep enforcing its focus trap over it.
    setModalEpoch(e => e + 1);
    // The open box unmounts with the new chat and a fresh one mounts:
    // queue the prompt for the fresh box instead of sending it through the
    // old box's `send`, which would land it in the conversation replaced.
    outbox.current.pending = prompt;
  };

  React.useEffect(() => {
    // Only the shown panel accepts a handed-off prompt: while `enabled` is
    // false the panel renders nothing, so asking it anything is a no-op.
    if (!access.enabled) return;
    return subscribeToAgentPanelAsk((prompt, options) => {
      setIsOpen(true);
      setEverOpened(true);
      if (options?.newConversation) askInNewChatRef.current(prompt);
      else askRef.current(prompt);
    });
  }, [access.enabled]);

  const activePane =
    agentPanelPanes.find(pane => pane.id === activePaneId) ?? null;

  const sessionCost = summarizeSessionCost(
    chat.messages.flatMap(m => {
      if (m.role !== "assistant" || m.costUsd === undefined) return [];
      const liveSpeechUsd = m.id ? (speechCostByMessageId[m.id] ?? null) : null;
      const settledCostUsd = m.id
        ? (settledCostUsdByMessageId.current[m.id] ?? null)
        : null;
      const split = splitAnswerCost(
        m.costUsd,
        resolveStoredSpeechCostUsd(settledCostUsd, m.costUsd, liveSpeechUsd),
        liveSpeechUsd
      );
      return [{ modelUsd: split.modelUsd, speechUsd: split.speechUsd }];
    })
  );

  // Same size as the views' own floating "+" (ProjectAddEntityButton): large
  // from tablet width on, medium on phones. The launcher is only there while
  // the panel is closed (and the assistant available); floating view actions
  // sit above it through the offset published here.
  const { isMinTablet } = useScreenWidth();
  const launcherSize = isMinTablet ? "large" : "medium";
  // Same breakpoint as Page's phone footer, so the margin matches its "+".
  const isMaxSm = useMediaQuery(
    outerTheme.breakpoints.down(outerTheme.breakpoints.values.sm),
    { noSsr: true }
  );
  usePublishAssistantLauncherOffset(
    access.enabled && !isOpen,
    isMinTablet ? 56 : 48
  );

  if (!access.enabled) return null;

  return (
    <>
      {!isOpen && (
        <Tooltip title={t("Open assistant")}>
          <Fab
            color="primary"
            size={launcherSize}
            aria-label={t("Open assistant")}
            onClick={openPanel}
            // Right edge aligned with the floating "+" of the Page layout
            // (same margin), which then docks straight above it.
            sx={{
              position: "fixed",
              right: outerTheme.spacing(floatActionMarginRight(isMaxSm)),
              bottom: 24,
              zIndex: 1300,
            }}
          >
            <AssistantIcon />
          </Fab>
        </Tooltip>
      )}
      {/*
       * A plain MUI `Modal` (not `@msys/ui`'s), non-blocking: no backdrop,
       * no scroll lock, and a root that lets clicks through
       * (`pointerEvents: "none"`) everywhere but the panel itself, so a
       * dialog or page underneath stays usable beside it. It is a real
       * Modal because MUI enforces a dialog's focus trap only for the
       * topmost modal *registered* with its ModalManager: a bare `Paper`
       * (as before) was fenced out by any open dialog's trap and could not
       * be typed into. Registered and above `theme.zIndex.modal`, the panel
       * sits on top and the dialog stops pulling the focus back; the panel
       * itself never traps it (`disableEnforceFocus`). Trade-off of any MUI
       * modal: while open, the rest of the page is marked `aria-hidden`.
       * `keepMounted` keeps the chat (composer draft included) alive while
       * the panel is closed; `modalEpoch` remounts the layer so it becomes
       * the topmost modal again (see the `newConversation` hand-off).
       * `keepMounted` only hides the closed layer once MUI's `exited` flag
       * is set, and that flag starts as `!open` and is only ever flipped by
       * a transitioning child's `onExited` (`useModal`). The layer mounts
       * open (first open, and every `modalEpoch` remount), so with a plain
       * `Paper` child `exited` stayed false and the X closed nothing
       * visible; the `Fade` gives the Modal that transition.
       */}
      {everOpened && (
        <Modal
          key={modalEpoch}
          open={isOpen}
          keepMounted
          hideBackdrop
          disableScrollLock
          disableEnforceFocus
          onClose={(_event, reason) => {
            if (reason === "escapeKeyDown") closePanel();
          }}
          sx={{
            zIndex: panelZIndex,
            pointerEvents: "none",
          }}
        >
          <Fade in={isOpen}>
            <Paper
              ref={setPanelElement}
              // The app theme defaults Paper to `outlined` (flat); a floating
              // panel needs the real elevation shadow to stand off the page.
              variant="elevation"
              elevation={8}
              role="dialog"
              aria-label={t("Assistant")}
              {...dropHandlers}
              sx={{
                pointerEvents: "auto",
                position: "fixed",
                right: panel.geometry.expanded
                  ? PANEL_MARGIN
                  : panel.geometry.right,
                bottom: panel.geometry.expanded
                  ? PANEL_MARGIN
                  : panel.geometry.bottom,
                width: panel.geometry.expanded ? 960 : panel.geometry.width,
                maxWidth: "calc(100vw - 32px)",
                height: panel.geometry.expanded
                  ? "calc(100vh - 32px)"
                  : panel.geometry.height,
                maxHeight: "calc(100vh - 32px)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                outline: "none",
              }}
            >
              {/* Only the content is wrapped — see `raiseOverlayZIndex`. */}
              <ThemeProvider theme={overlayTheme}>
                {dragDepth > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 8,
                      zIndex: 2,
                      border: 2,
                      borderStyle: "dashed",
                      borderColor: "primary.main",
                      borderRadius: 1,
                      bgcolor: "background.paper",
                      opacity: 0.95,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <Typography variant="subtitle2" color="primary">
                      {t("Drop files here", { ns: "AttachmentField" })}
                    </Typography>
                  </Box>
                )}

                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "center",
                    px: 1.5,
                    py: 1,
                    borderBottom: 1,
                    borderColor: "divider",
                    bgcolor: agentHeaderBackground,
                    userSelect: "none",
                  }}
                >
                  {!panel.geometry.expanded && (
                    <Tooltip title={t("Resize panel")}>
                      <Box
                        component="button"
                        type="button"
                        {...panel.resizeHandlers}
                        aria-label={t("Resize panel")}
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: 20,
                          height: 20,
                          touchAction: "none",
                          p: 0,
                          border: 0,
                          bgcolor: "transparent",
                          cursor: "nwse-resize",
                          zIndex: 1,
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 3,
                            left: 3,
                            width: 8,
                            height: 8,
                            borderTop: 2,
                            borderLeft: 2,
                            borderColor: "text.disabled",
                          },
                        }}
                      />
                    </Tooltip>
                  )}
                  {/* Only the handle and the title move the panel; the buttons stay buttons. */}
                  <Tooltip
                    title={
                      panel.geometry.expanded
                        ? ""
                        : t(
                            "Drag to move · double-click to return to the corner"
                          )
                    }
                  >
                    {/* Shrinks no further than its icons (the label has `width: 0`
            and only fills the room left over), so a chip never slides over
            the drag handle. */}
                    <Stack
                      data-testid="agent-panel-header-title"
                      direction="row"
                      spacing={1}
                      {...panel.moveHandlers}
                      onDoubleClick={panel.resetPosition}
                      sx={{
                        flex: 1,
                        alignItems: "center",
                        cursor: panel.geometry.expanded ? undefined : "move",
                        touchAction: "none",
                      }}
                    >
                      {!panel.geometry.expanded && (
                        <DragIcon
                          fontSize="small"
                          sx={{ color: "text.disabled" }}
                        />
                      )}
                      <AssistantIcon fontSize="small" color="primary" />
                      <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{ flexGrow: 1, width: 0 }}
                      >
                        {t("Assistant")}
                      </Typography>
                    </Stack>
                  </Tooltip>
                  {/* Never wider than the panel: once the title is down to its icons,
            this group gives up the rest of the squeeze through its two
            chips (`minWidth: 0`, ellipsized labels), while every button —
            Close last — keeps its size. A header wider than the panel
            would push Close out of view and let the panel scroll its
            content sideways when focus moves. */}
                  <Box
                    data-testid="agent-panel-header-actions"
                    sx={{
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      "& > .MuiChip-root": { minWidth: 0 },
                      "& > .MuiIconButton-root": { flexShrink: 0 },
                    }}
                  >
                    <Tooltip title={context.path}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t("Context: {context}", {
                          ns: "Assistant",
                          context: context.path,
                        })}
                        sx={{ maxWidth: 220 }}
                      />
                    </Tooltip>
                    {sessionCost.answered > 0 && (
                      <Tooltip
                        title={t(
                          "{answered} answers · {unknown} without a reported cost",
                          {
                            ns: "Assistant",
                            answered: sessionCost.answered,
                            unknown: sessionCost.unknown,
                          }
                        )}
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t("Σ {amount} ct", {
                            ns: "Assistant",
                            amount: getFormattedFloat(
                              sessionCost.totalUsd * 100
                            ),
                          })}
                        />
                      </Tooltip>
                    )}
                    <Tooltip
                      title={
                        historyOpen
                          ? t("Hide conversation history")
                          : historyActivity || t("Conversation history")
                      }
                    >
                      <IconButton
                        size="small"
                        color={historyOpen ? "primary" : "default"}
                        aria-label={t("Conversation history")}
                        aria-expanded={historyOpen}
                        onClick={toggleHistory}
                      >
                        <Badge
                          color={unseenFinished > 0 ? "error" : "primary"}
                          variant={unseenFinished > 0 ? "standard" : "dot"}
                          // Left out (not 0) without a finished answer: MUI
                          // hides a badge whose content is 0, which would
                          // swallow the "running" dot.
                          badgeContent={
                            unseenFinished > 0 ? unseenFinished : undefined
                          }
                          invisible={
                            historyOpen ||
                            (runningElsewhere === 0 && unseenFinished === 0)
                          }
                        >
                          <HistoryIcon fontSize="small" />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                    {agentPanelPanes.map(pane => (
                      <PaneHeaderButton
                        key={pane.id}
                        pane={pane}
                        active={activePaneId === pane.id}
                        onToggle={id =>
                          setActivePaneId(current =>
                            current === id ? null : id
                          )
                        }
                      />
                    ))}
                    <Tooltip title={t("New conversation")}>
                      <IconButton
                        size="small"
                        aria-label={t("New conversation")}
                        onClick={newChat}
                      >
                        <NewChatIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        panel.geometry.expanded
                          ? t("Collapse", { ns: "Global" })
                          : t("Expand", { ns: "Global" })
                      }
                    >
                      <IconButton
                        size="small"
                        aria-label={
                          panel.geometry.expanded
                            ? t("Collapse", { ns: "Global" })
                            : t("Expand", { ns: "Global" })
                        }
                        onClick={panel.toggleExpanded}
                      >
                        {panel.geometry.expanded ? (
                          <CloseFullscreenIcon fontSize="small" />
                        ) : (
                          <OpenInFullIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      aria-label={t("Close", { ns: "Global" })}
                      onClick={closePanel}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Stack>

                <Stack sx={{ flex: 1, minHeight: 0 }}>
                  {/* Kept mounted (just hidden) while a pane is open, so a running
            stream/composer state survives switching back to the chat. */}
                  <Stack
                    sx={{ flex: 1, minHeight: 0 }}
                    style={activePane ? { display: "none" } : undefined}
                  >
                    <AgentChatBox
                      // A new box per new chat, or per identity switch (`identityKey`,
                      // already used to reset `useAgentChat` via `resetKey`): it reads
                      // its start state only on mount, so it must remount on either to
                      // stop showing a prior identity's conversation. `useAgentChat`
                      // answers empty in the very render the key changes, so the
                      // remounted box never seeds the prior identity's messages.
                      key={`${chatEpoch}:${identityKey}`}
                      chat={chat}
                      context={context}
                      outbox={outbox}
                      onAttachmentsRejected={setRejectedAttachments}
                      showConversations={
                        listLayout === "sidebar" && showConversations
                      }
                      conversationOverlayOpen={
                        listLayout === "overlay" && overlayRequested
                      }
                      onConversationOverlayClose={closeConversationOverlay}
                      focusComposerOnMount={focusEpoch === chatEpoch}
                      onConversation={id => {
                        // Showing another conversation ends any reading; the
                        // shown chat naming its own conversation does not.
                        if (id !== chat.conversationId) speech.stop();
                      }}
                      speech={speech}
                      speechCostByMessageId={speechCostByMessageId}
                      costOverrideByMessageId={costOverrideByMessageId}
                    />
                    {rejectedAttachments && (
                      <Alert
                        severity="warning"
                        onClose={() => setRejectedAttachments(null)}
                      >
                        {describeRejections(rejectedAttachments, t)}
                      </Alert>
                    )}
                    {chat.conflict && (
                      <Alert severity="info">
                        {t(
                          "The assistant is still working on your last message."
                        )}
                      </Alert>
                    )}
                    {chat.error && (
                      <Alert severity="error">
                        {t("The assistant could not answer. Please try again.")}
                      </Alert>
                    )}
                  </Stack>
                  {activePane && (
                    <Box
                      sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 1.5 }}
                    >
                      <activePane.Component />
                    </Box>
                  )}
                </Stack>
              </ThemeProvider>
            </Paper>
          </Fade>
        </Modal>
      )}
    </>
  );
}
